// «Импорт из Panda-Manda» в настройках: пока лендинги живут на panda-manda.uz, лиды из кабинета buyo приходят
// сюда без полного номера. Владелец выгружает базу CPA из phpMyAdmin и загружает файл — лиды получают полные
// номера и имена. Файл разбирается в памяти api и не сохраняется (в нём секреты старой системы).
import { Button, Card, Chip, int, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, History, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { fileSize } from './BackupCard';

interface PandaImport {
  state: 'running' | 'done' | 'error';
  startedAt: string;
  finishedAt: string | null;
  bytes: number;
  stats: {
    offers: { created: number; updated: number };
    leads: { created: number; updated: number; merged: number; skipped: number };
    events: number;
  } | null;
  /** Лидов с полным номером в платформе после импорта. */
  phones: number | null;
  error: string | null;
}

const MAX_BYTES = 50 * 1024 * 1024;

/** Размер файла — как везде в платформе («6.5 МБ»). */
const size = fileSize;

/** Итог словами: сколько лидов пришло новых, обновилось, слилось с лидами из кабинета buyo. */
function summary(s: NonNullable<PandaImport['stats']>, phones: number | null): string {
  const leads = [
    s.leads.created ? `новых ${int(s.leads.created)}` : null,
    s.leads.updated ? `обновлено ${int(s.leads.updated)}` : null,
    s.leads.merged ? `дополнено из кабинета buyo ${int(s.leads.merged)}` : null,
  ].filter(Boolean);
  const offers = s.offers.created ? `, новых офферов ${int(s.offers.created)}` : '';
  const total = phones !== null ? ` Лидов с полным номером в платформе: ${int(phones)}.` : '';
  return `Лиды: ${leads.length ? leads.join(', ') : 'без изменений'}${offers}.${total}`;
}

export function PandaImportCard() {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['import', 'panda-manda'],
    queryFn: () => api<PandaImport | null>('/api/import/panda-manda'),
    // Пока идёт импорт — спрашиваем итог чаще
    refetchInterval: (query) => (query.state.data?.state === 'running' ? 1500 : false),
  });
  const st = q.data;
  const running = sending || st?.state === 'running';

  // Импорт закончился — у лидов появились номера и имена: освежить списки лидов
  const prev = useRef(st?.state);
  useEffect(() => {
    if (prev.current === 'running' && st?.state === 'done') {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead'] });
    }
    prev.current = st?.state;
  }, [st?.state, qc]);

  const upload = async (file: File) => {
    setError(null);
    if (file.size > MAX_BYTES) {
      setError('Файл больше 50 МБ. В phpMyAdmin выберите сжатие gzip — файл станет в 5–10 раз меньше.');
      return;
    }
    setSending(true);
    try {
      const next = await api<PandaImport>('/api/import/panda-manda', file);
      qc.setQueryData(['import', 'panda-manda'], next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const last = !st
    ? 'Из настроек ещё не загружали.'
    : st.state === 'running'
      ? `Переношу лиды из файла ${size(st.bytes)}… Обычно это несколько секунд.`
      : st.state === 'done' && st.stats
        ? `${when(st.finishedAt ?? st.startedAt)} · файл ${size(st.bytes)}. ${summary(st.stats, st.phones)}`
        : `${when(st.finishedAt ?? st.startedAt)} · не получилось.`;

  return (
    <Card title="Импорт из Panda-Manda">
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 'var(--fs-sm)' }}>
        Пока лендинги работают на panda-manda.uz, лиды из кабинета buyo приходят сюда без полного номера.
        Загрузите свежую выгрузку базы — лиды получат полные номера и имена, их увидите вы и агент. Уже
        перенесённое не задвоится.
      </p>
      <ul className="backup-list">
        <li>
          <FileDown aria-hidden="true" />
          <div className="min-w-0">
            <b>Где взять файл</b>
            <span>
              phpMyAdmin → слева база CPA (не магазина) → вкладка «Экспорт» → формат SQL → экспортировать.
              Подойдёт .sql или .sql.gz до 50 МБ.
            </span>
          </div>
        </li>
        <li>
          <History aria-hidden="true" />
          <div className="min-w-0">
            <b>Последняя загрузка</b>
            {q.isPending ? (
              <div className="skeleton" style={{ height: 18, marginTop: 4 }} />
            ) : q.isError ? (
              <span>Не удалось узнать — обновите страницу.</span>
            ) : (
              <span aria-live="polite">{last}</span>
            )}
          </div>
          {st?.state === 'running' ? (
            <Chip tone="info">Идёт</Chip>
          ) : st?.state === 'done' ? (
            <Chip tone="success" dot>
              Готово
            </Chip>
          ) : st?.state === 'error' ? (
            <Chip tone="danger">Ошибка</Chip>
          ) : null}
        </li>
      </ul>
      {(error ?? (st?.state === 'error' ? st.error : null)) && (
        <p className="notice error" role="alert" style={{ margin: '12px 0 0' }}>
          {error ?? st?.error}
        </p>
      )}
      <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 14 }}>
        <input
          ref={input}
          type="file"
          accept=".sql,.gz,application/sql,application/gzip"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = '';
          }}
        />
        <Button variant="primary" icon={<Upload />} disabled={running} onClick={() => input.current?.click()}>
          {sending ? 'Загружаю файл…' : running ? 'Импорт идёт…' : 'Загрузить дамп (.sql)'}
        </Button>
        <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
          Файл разбирается в памяти и не сохраняется: в нём пароли и ключи старой системы.
        </span>
      </div>
    </Card>
  );
}
